import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import GeetanjaliLogo from "./GeetanjaliLogo";
import ErrorBoundary from "./ErrorBoundary";
import {
  Gauge,
  Crown,
  ClipboardList,
  Users,
  Upload,
  Coins,
  UserCheck,
  TrendingUp,
  Boxes,
  ClipboardCheck,
  FlaskConical,
  ShieldAlert,
  ShoppingCart,
  Building2,
  Wallet,
  Landmark,
  FileSpreadsheet,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

// The sidebar is a table of contents, not a pile of links: sections, each
// with the subsets that belong to it and nothing else. Sections and subsets
// are always open — a manager should be able to see the whole shape of the
// system without clicking to discover it — and every item carries the roles
// that may see it, so a manager is never shown a door that 403s behind them.
//
// ORDER (owner, 27 Oct 2026): every MIS / reporting page comes first, then
// the desks that run the day, then the operational sections. The reporting
// block is deliberately at the top — that is what the owner opens the system
// to read. Owner MIS sits under Dashboards (owner's choice the same day) so
// the reporting block stays purely analysis; Manager Desk remains manager-only.
const OWNER = ["owner"];
const BOTH = ["owner", "manager"];

const navCategories = [
  {
    id: "dashboards",
    title: "Dashboards",
    icon: Gauge,
    items: [
      { to: "/owner", label: "Owner MIS", icon: Crown, roles: OWNER },
      { to: "/manager", label: "Manager Desk", icon: ClipboardList, roles: ["manager"] },
    ],
  },
  {
    id: "reports",
    title: "MIS & Reports",
    icon: FileSpreadsheet,
    items: [
      { to: "/analytics", label: "Sales Analytics", icon: TrendingUp, roles: BOTH },
      { to: "/pnl", label: "Profit & Loss", icon: FileSpreadsheet, roles: BOTH },
    ],
  },
  {
    id: "sales",
    title: "Sales & Team",
    icon: Users,
    items: [
      { to: "/pos", label: "POS Import", icon: Upload, roles: BOTH },
      { to: "/attendance", label: "Staff & Attendance", icon: UserCheck, roles: BOTH },
      { to: "/incentives", label: "Incentives Engine", icon: Coins, roles: BOTH },
    ],
  },
  {
    id: "inventory",
    title: "Inventory & Stock",
    icon: Boxes,
    items: [
      { to: "/inventory", label: "Inventory", icon: Boxes, roles: BOTH },
      { to: "/audit", label: "Stock Audit", icon: ClipboardCheck, roles: BOTH },
      { to: "/cogs", label: "COGS & Recipes", icon: FlaskConical, roles: BOTH },
      { to: "/quality", label: "Quality Control", icon: ShieldAlert, roles: BOTH },
    ],
  },
  {
    id: "procurement",
    title: "Procurement",
    icon: ShoppingCart,
    items: [
      { to: "/procurement", label: "Purchase Orders", icon: ShoppingCart, roles: BOTH },
      { to: "/vendors", label: "Vendor Matrix", icon: Building2, roles: OWNER },
    ],
  },
  {
    id: "finance",
    title: "Finance",
    icon: Landmark,
    items: [
      { to: "/cashbook", label: "Daily Cash Book", icon: Wallet, roles: BOTH },
      { to: "/banking", label: "Bank Reconciliation", icon: Landmark, roles: BOTH },
    ],
  },
  {
    id: "settings",
    title: "Administration",
    icon: Settings,
    items: [
      { to: "/config", label: "Master Config", icon: Settings, roles: OWNER },
    ],
  },
];

// One predicate for "may this person see this item" — the expanded menu and
// the collapsed icon rail both ask it, so the two can never disagree.
const canSee = (item, role) => !role || item.roles.includes(role);

// Exported because App.js guards the routes from this same table: a page the
// sidebar hides must be a page the router refuses, or the menu is decoration
// rather than access control.
export { navCategories };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const hoverTimer = useRef(null);

  const handleMouseEnter = () => {
    if (isPinned) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (isPinned) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setIsHovered(false);
  };

  // All main tabs stay open permanently: every subset is always visible,
  // the headers are labels, not toggles.

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  // The collapsed icon rail is the same six sections with the labels taken
  // off, so the sections are marked by a hairline instead — otherwise a
  // column of 17 identical icons reads as one undifferentiated pile.
  const rail = [];
  navCategories.forEach((cat) => {
    const items = cat.items.filter((it) => canSee(it, user?.role));
    if (!items.length) return;
    if (rail.length) rail.push({ sep: true, key: `sep-${cat.id}` });
    items.forEach((it) => rail.push(it));
  });

  return (
    <div className="flex flex-col lg:flex-row min-h-screen premium-bg text-slate-950 font-sans selection:bg-amber-400 selection:text-slate-950">
      {/* Mobile Top Navbar with Hamburger Toggle */}
      <header className="lg:hidden flex items-center justify-between px-4 py-3.5 bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-xs">
        <GeetanjaliLogo size="sm" />
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-slate-700 hover:text-slate-950 rounded-lg hover:bg-slate-100 transition"
          aria-label="Toggle Navigation Menu"
        >
          {mobileOpen ? <X className="w-6 h-6 text-slate-950" /> : <Menu className="w-6 h-6 text-slate-950" />}
        </button>
      </header>

      {/* Backdrop overlay for mobile drawer */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-slate-950/40 backdrop-blur-xs z-40 lg:hidden"
        />
      )}

      {/* Sidebar (Fixed/Sticky on Desktop, Hover-expanding / Pin-toggle, Slide-over Drawer on Mobile) */}
      <aside
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`fixed inset-y-0 left-0 z-50 h-full bg-white/25 backdrop-blur-2xl border-r border-[#E6DACD] flex flex-col transition-[width,transform] duration-100 ease-out shadow-xl lg:sticky lg:top-0 lg:h-screen lg:shrink-0 lg:translate-x-0 ${
          mobileOpen ? "w-64 translate-x-0" : `-translate-x-full lg:translate-x-0 ${isPinned || isHovered ? "lg:w-64" : "lg:w-20"}`
        }`}
      >
        {/* Middle-Right Pin Toggle Arrow Button */}
        <button
          type="button"
          onClick={() => {
            if (isPinned) {
              setIsPinned(false);
              setIsHovered(false);
            } else {
              setIsPinned(true);
              setIsHovered(true);
            }
          }}
          title={isPinned ? "Unpin sidebar (auto-collapse)" : "Pin sidebar open"}
          className="hidden lg:flex absolute top-1/2 -right-3.5 -translate-y-1/2 w-7 h-7 bg-white border border-[#E6DACD] rounded-full shadow-md items-center justify-center text-slate-700 hover:text-slate-950 hover:scale-110 hover:border-amber-400 transition-all z-50 cursor-pointer"
        >
          {isPinned ? (
            <ChevronLeft className="w-4 h-4 text-amber-800" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-600" />
          )}
        </button>

        {/* Header Logo */}
        <div className={`py-5 border-b border-[#E6DACD] flex items-center shrink-0 bg-transparent transition-[padding,justify-content] duration-100 ease-out ${
          isPinned || isHovered || mobileOpen ? "px-5 justify-between" : "px-3 justify-center"
        }`}>
          <GeetanjaliLogo size="md" collapsed={!isPinned && !isHovered && !mobileOpen} />
          {mobileOpen && (
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden p-1 text-slate-600 hover:text-slate-950"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Nav Body */}
        <nav className="flex-1 py-3 px-2 overflow-y-auto space-y-3 min-h-0 custom-scrollbar">
          <AnimatePresence>
            {isPinned || isHovered || mobileOpen ? (
              /* Expanded Full Menu */
              <motion.div
                key="expanded-menu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.08, ease: "easeOut" }}
                className="space-y-3"
              >
                {navCategories.map((cat) => {
              const allowedItems = cat.items.filter((it) => canSee(it, user?.role));
              if (allowedItems.length === 0) return null;

              const hasActiveSub = allowedItems.some((it) => it.to === location.pathname);

              return (
                <div key={cat.id} className="space-y-1">
                  {/* Category Header (label — subsets stay open permanently) */}
                  <div
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 text-[11px] font-extrabold uppercase tracking-wider rounded-lg ${
                      hasActiveSub ? "text-amber-800" : "text-slate-500"
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <cat.icon className={`w-3.5 h-3.5 shrink-0 ${hasActiveSub ? "text-amber-700" : "text-slate-400"}`} />
                      <span className="truncate">{cat.title}</span>
                    </div>
                    <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-slate-100 text-slate-600 shrink-0">
                      {allowedItems.length}
                    </span>
                  </div>

                  {/* Sub Links (always visible) */}
                  <div className="space-y-1 pl-1">
                    {allowedItems.map((it) => (
                          <NavLink
                            key={it.to}
                            to={it.to}
                            onClick={() => setMobileOpen(false)}
                            data-testid={`nav-${it.to.replace("/", "")}`}
                            className={({ isActive }) =>
                              `relative flex items-center gap-2.5 px-3 py-2.5 text-xs rounded-xl transition-all ${
                                isActive
                                  ? "bg-slate-950 text-amber-300 font-extrabold border-l-4 border-amber-400 shadow-md shadow-slate-950/20"
                                  : "text-slate-700 hover:text-slate-950 hover:bg-slate-100/70 font-semibold"
                              }`
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <it.icon className={`w-4 h-4 transition-transform duration-200 shrink-0 ${isActive ? "text-amber-400" : "text-slate-400"}`} />
                                <span className="truncate">{it.label}</span>
                              </>
                            )}
                          </NavLink>
                        ))}
                  </div>
                </div>
              );
            })}
          </motion.div>
        ) : (
          /* Collapsed Mini Icons Bar */
          <motion.div
            key="collapsed-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="space-y-2.5 flex flex-col items-center"
          >
            {rail.map((it) => {
              if (it.sep) {
                return <hr key={it.key} className="border-0 border-t border-slate-300/70 w-9 my-1 shrink-0" />;
              }
              const isActive = location.pathname === it.to;
              return (
                <div key={it.to} className="relative group/tooltip">
                  <NavLink
                    to={it.to}
                    className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      isActive
                        ? "bg-slate-950 text-amber-300 border-l-4 border-amber-400 shadow-md shadow-slate-950/25 scale-105"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                    }`}
                  >
                    <it.icon className={`w-5 h-5 ${isActive ? "text-amber-400" : ""}`} />
                  </NavLink>

                  {/* Floating Tooltip Label Badge */}
                  <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 hidden group-hover/tooltip:flex items-center z-50 pointer-events-none">
                    <div className="bg-slate-950 text-amber-300 font-extrabold text-xs px-3 py-1.5 rounded-xl shadow-xl border border-amber-400/40 whitespace-nowrap animate-in fade-in zoom-in-95 duration-150">
                      {it.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>

        {/* Footer User Info / Sign Out */}
        <div className={`border-t border-[#E6DACD] bg-white/20 shrink-0 transition-all ${
          isPinned || isHovered || mobileOpen ? "p-4" : "p-2 flex flex-col items-center justify-center"
        }`}>
          {isPinned || isHovered || mobileOpen ? (
            <>
              <div className="text-xs text-slate-600 mb-3 flex items-center justify-between">
                <div>
                  <div className="text-slate-950 font-extrabold text-sm leading-snug">{user?.name || "Salon Owner"}</div>
                  <div className="text-[10px] font-extrabold uppercase tracking-wider text-amber-800 mt-0.5">{user?.role || "OWNER"}</div>
                </div>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 border border-white shadow-xs"></span>
              </div>
              <button
                data-testid="logout-btn"
                onClick={doLogout}
                className="w-full py-2.5 px-3 text-xs font-bold text-slate-950 bg-white border border-slate-300 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 rounded-xl transition-all flex items-center justify-center gap-2 shadow-xs"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>
            </>
          ) : (
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              title="Sign out"
              className="w-10 h-10 rounded-2xl bg-white border border-slate-300 text-slate-700 hover:bg-rose-50 hover:text-rose-700 flex items-center justify-center shadow-xs"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </aside>

      {/* Main Outlet with Framer Motion Page Fade & Slide Transition */}
      <main className="flex-1 min-w-0 overflow-x-hidden bg-transparent relative z-10">
        <ErrorBoundary key={location.pathname}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
          >
            <Outlet />
          </motion.div>
        </ErrorBoundary>
      </main>
    </div>
  );
}
